import { USER_LANGUAGE, USER_TYPE, VALIDITY_TYPE } from "../constant";
import { Entity } from "./entity.model";
import { Invite } from "./invite.model";
import { Role } from "./role.model";
import { UserGroup } from "./user-group.model";
import { DeviceRight } from "./device-right.model";

export enum UserStatus {
  Pending = "Pending",
  Expired = "Expired",
  Active = "Active",
  ValidityExpired = "ValidityExpired",
  Deactivated = "Deactivated",
}

export type UserCompany = {
  companyId: number;
  active: boolean;
  userId: number;
  name: string;
  status: UserStatus;
};

export class User extends Entity {
  email: string;
  type: USER_TYPE;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLogin: string;
  active: boolean;
  permissions: string[];
  roles: Role[];
  deviceRights: DeviceRight[];
  groups: UserGroup[] | null;
  accepted: boolean;
  status: UserStatus;
  invites: Invite[];
  language: keyof typeof USER_LANGUAGE;
  expiresAt: string | null;
  validityType: VALIDITY_TYPE;
  acceptedPrivacy: boolean;
  acceptedPrivacyAt: Date | null;
  phone?: string;
  userCompanies?: UserCompany[];

  constructor({
    email,
    type,
    firstName,
    lastName,
    createdAt,
    lastLogin,
    active,
    id,
    permissions,
    roles,
    deviceRights,
    groups,
    accepted,
    invites,
    language,
    expiresAt,
    status,
    acceptedPrivacy,
    acceptedPrivacyAt,
    phone,
    userCompanies,
  }: {
    email: string;
    type: USER_TYPE;
    firstName: string;
    lastName: string;
    createdAt: string;
    lastLogin: string;
    active: boolean;
    id: number;
    permissions: string[];
    roles: Role[];
    deviceRights: DeviceRight[];
    groups: UserGroup[] | null;
    accepted: boolean;
    invites: Invite[];
    language: keyof typeof USER_LANGUAGE;
    expiresAt: string | null;
    status: UserStatus;
    acceptedPrivacy: boolean;
    acceptedPrivacyAt: Date | null;
    phone?: string;
    userCompanies?: UserCompany[];
  }) {
    super(id);
    this.email = email;
    this.type = type;
    this.firstName = firstName;
    this.lastName = lastName;
    this.createdAt = createdAt;
    this.lastLogin = lastLogin;
    this.active = active;
    this.permissions = permissions;
    this.roles = (roles || []).map((role) => new Role(role));
    this.deviceRights = (deviceRights || []).map((dr) => new DeviceRight(dr));
    this.groups = groups
      ? groups.map((group) => new UserGroup(group || []))
      : null;
    this.invites = (invites || []).map((invite) => new Invite(invite));
    this.accepted = accepted;
    this.language = language;
    this.expiresAt = expiresAt;
    this.validityType = expiresAt
      ? VALIDITY_TYPE.FIXED
      : VALIDITY_TYPE.CONTINUOUS;
    this.status = status;
    this.acceptedPrivacy = acceptedPrivacy;
    this.acceptedPrivacyAt = acceptedPrivacyAt;
    this.phone = phone;
    this.userCompanies = userCompanies;
  }

  static getStatus(current: {
    accepted: boolean;
    active: boolean;
    invite: Invite[];
  }): UserStatus {
    if (!current.accepted && current.active) {
      return current.invite.some(
        (invite) => new Date(invite.expiredAt).getTime() > new Date().getTime(),
      )
        ? UserStatus.Pending
        : UserStatus.Expired;
    }
    if (current.active) {
      return UserStatus.Active;
    }
    return UserStatus.Deactivated;
  }

  get fullName() {
    return `${this.firstName || ""} ${this.lastName || ""}`.trim();
  }
}

export class UserEmail {
  email: string;

  constructor({ email }: { email: string }) {
    this.email = email;
  }
}
